/**
 * Set Password Page
 * For new teachers to set their initial password
 * Accessed via link in welcome email
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useNotification } from '@/hooks/useNotification';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';

export const SetPasswordPage = () => {
  const { forgotPassword, resetPassword } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<'request' | 'reset'>('request');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Pre-fill email from URL parameter if provided
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [searchParams]);

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      showNotification('error', 'Please enter your email address');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await forgotPassword(email);
      
      if (response.success) {
        showNotification('success', 'Verification code sent to your email');
        setStep('reset');
      } else {
        showNotification('error', response.error || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Request code error:', error);
      showNotification('error', 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!verificationCode || !newPassword) {
      showNotification('error', 'Please fill in all fields');
      return;
    }

    // Validate password requirements
    if (newPassword.length < 8) {
      showNotification('error', 'Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      showNotification('error', 'Password must contain at least one uppercase letter');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      showNotification('error', 'Password must contain at least one lowercase letter');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      showNotification('error', 'Password must contain at least one number');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await resetPassword(email, verificationCode, newPassword);
      
      if (response.success) {
        showNotification('success', 'Password set successfully! Redirecting to login...');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        showNotification('error', response.error || 'Failed to set password');
      }
    } catch (error) {
      console.error('Set password error:', error);
      showNotification('error', 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
            <img src="/admin/icon.png" alt="SpellTales" className="w-16 h-16 rounded-2xl" />
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mb-2">
            Welcome to SpellTales!
          </h1>
          <p className="text-slate-600">Set your password to get started</p>
        </div>

        {/* Set Password Form */}
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-8">
          {step === 'request' && (
            <form onSubmit={handleRequestCode} className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-slate-900 mb-2">Get Started</h2>
                <p className="text-sm text-slate-600">
                  Enter your email to receive a verification code
                </p>
              </div>

              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="teacher@school.edu"
                required
                autoComplete="email"
                autoFocus
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isLoading}
                className="w-full"
              >
                Send Verification Code
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm text-slate-600 hover:text-slate-900"
                >
                  Already have a password? Sign in
                </button>
              </div>
            </form>
          )}

          {step === 'reset' && (
            <form onSubmit={handleSetPassword} className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-slate-900 mb-2">Create Your Password</h2>
                <p className="text-sm text-slate-600">
                  Enter the verification code sent to {email}
                </p>
              </div>

              <Input
                label="Verification Code"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                required
                autoComplete="off"
                autoFocus
              />

              <Input
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="new-password"
              />

              <div className="text-xs text-slate-600 space-y-1 bg-slate-50 p-3 rounded-lg">
                <p className="font-semibold">Password must contain:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>At least 8 characters</li>
                  <li>One uppercase letter</li>
                  <li>One lowercase letter</li>
                  <li>One number</li>
                </ul>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isLoading}
                className="w-full"
              >
                Set Password
              </Button>

              <div className="text-center space-y-2">
                <button
                  type="button"
                  onClick={() => setStep('request')}
                  className="block w-full text-sm text-slate-600 hover:text-slate-900"
                >
                  Didn't receive code? Request new code
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="block w-full text-sm text-slate-600 hover:text-slate-900"
                >
                  Back to Sign In
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-slate-500">
          <p>© 2026 SpellTales. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};
